import { FC, useState, useEffect, useCallback, useRef } from 'react';

import { useTranslationRef } from '@backstage/core-plugin-api/alpha';

import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import { Textfield } from '@xlrt/components';
import { useConversationSearchBarStyles } from './styles/conversation-search-bar.styles';
import { mcpChatTranslationRef } from '../../translation';

/** Debounce delay in milliseconds */
const DEBOUNCE_DELAY = 300;

interface ConversationSearchBarProps {
  /** Current search value */
  value: string;
  /** Called when search value changes (debounced) */
  onChange: (value: string) => void;
  /** Called when search is cleared */
  onClear: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether to focus the search input on mount */
  focusOnMount?: boolean;
}

/**
 * Search bar component for filtering conversations.
 * Includes debouncing for smooth filtering.
 */
export const ConversationSearchBar: FC<ConversationSearchBarProps> = ({
  value,
  onChange,
  onClear,
  placeholder = 'Search chats',
  focusOnMount = false,
}) => {
  const classes = useConversationSearchBarStyles();
  const { t } = useTranslationRef(mcpChatTranslationRef);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input when focusOnMount is true
  useEffect(() => {
    if (focusOnMount && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focusOnMount]);

  // Sync local value with prop value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced onChange handler
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localValue, value, onChange]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(event.target.value);
    },
    [],
  );

  const handleClear = useCallback(() => {
    setLocalValue('');
    onClear();
  }, [onClear]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClear();
      }
    },
    [handleClear],
  );

  return (
    <Box className={classes.searchBarWrapper}>
      <Textfield
        fullWidth
        size="small"
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        ref={inputRef}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: localValue ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                sx={{ padding: 0.5, color: 'inherit' }}
                aria-label={t('conversationSearchBar.ariaLabels.clearSearch')}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  );
};
